import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Stale-deploy resilience for `React.lazy` route chunks.
 *
 * After a deploy, an already-open tab still holds the PREVIOUS build's
 * `index.html`, which references hashed chunk filenames (e.g.
 * `view-appointment-panel-page-B4hIArR5.js`) that no longer exist on the
 * server. Navigating to a not-yet-loaded lazy route fetches the dead hash
 * → the server's SPA fallback serves `index.html` (Content-Type
 * `text/html`) → the browser's strict MIME check rejects it → the dynamic
 * import rejects with "Failed to fetch dynamically imported module".
 *
 * With only `<Suspense>` (no error boundary) that rejection unmounts the
 * whole tree = white screen. This wrapper makes the FIRST such failure
 * trigger a single full reload, which re-fetches the fresh `index.html`
 * and the correct chunk hashes — recovering silently. A per-chunk
 * sessionStorage guard prevents a reload loop if the chunk is genuinely
 * broken (in that case we rethrow so `RouteErrorBoundary` shows a
 * graceful fallback rather than reloading forever).
 */

/** Best-effort detection of the dynamic-import / chunk-load failure across browsers. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) || // Firefox
    /Importing a module script failed/i.test(msg) || // Safari
    /Expected a JavaScript(-or-Wasm)? module script/i.test(msg) || // Chrome MIME
    /text\/html/i.test(msg) // SPA-fallback HTML served for a .js request
  );
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Storage can throw in some privacy modes — degrade to "no guard".
    return null;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React.lazy's own
// signature; `unknown` rejects any component that declares props (contravariance).
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  /** Stable key per chunk so each route gets its own reload guard. */
  key: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const session = safeSession();
    const guardKey = `scrips.chunk-reload.${key}`;
    try {
      const mod = await factory();
      session?.removeItem(guardKey); // recovered → allow a future reload
      return mod;
    } catch (err) {
      if (isChunkLoadError(err) && session && !session.getItem(guardKey)) {
        session.setItem(guardKey, '1');
        window.location.reload();
        // Keep Suspense in its loading state while the page reloads,
        // instead of throwing into the (empty) render path.
        return new Promise<{ default: T }>(() => {});
      }
      // Already reloaded once (or a non-chunk error) → surface it so the
      // error boundary can render the reload panel.
      throw err;
    }
  });
}
